import { type MouseEvent, memo } from 'react';
import clsx from 'clsx';

// DR-2427: primitive-prop subcomponents wrapped in memo so a hover-state change re-renders
// ONLY the cells/bands whose `dimmed` boolean actually flips — geometry props are stable
// (memoized descriptors) and callbacks are stable (useCallback), so memo's shallow compare
// short-circuits every unchanged node instead of re-rendering all ~thousands on each hover.

export const Y = { page: 12, pageH: 22, leaf: 46, leafH: 36, band: 92, bandH: 26 };

export type SlotProps = {
  index: number;
  dimmed: boolean;
  rectX: number;
  rectW: number;
  textX: number;
  segmented: boolean;
  dividerXs: number[];
  pageNo: number;
  showText: boolean;
  onEnter: (index: number, e: MouseEvent) => void;
  onOpen: (index: number, e: MouseEvent) => void;
};
export const SlotCell = memo(function SlotCell({
  index, dimmed, rectX, rectW, textX, segmented, dividerXs, pageNo, showText, onEnter, onOpen,
}: SlotProps) {
  return (
    <g
      className="cursor-pointer transition-opacity"
      style={{ opacity: dimmed ? 0.18 : 1 }}
      onMouseEnter={(e) => onEnter(index, e)}
      onClick={(e) => onOpen(index, e)}
      onAuxClick={(e) => onOpen(index, e)}
    >
      <rect
        x={rectX}
        y={Y.page}
        width={rectW}
        height={Y.pageH}
        rx={5}
        className={clsx(
          'stroke',
          segmented
            ? 'fill-violet-100 dark:fill-violet-900/40 stroke-violet-400 dark:stroke-violet-700'
            : 'fill-slate-100 dark:fill-zinc-700 stroke-slate-300 dark:stroke-zinc-600',
        )}
      />
      {dividerXs.map((dx, i) => (
        <line
          key={i}
          x1={dx}
          y1={Y.page + 3}
          x2={dx}
          y2={Y.page + Y.pageH - 3}
          className="stroke-violet-300 dark:stroke-violet-700"
          strokeDasharray="2 2"
        />
      ))}
      {showText && (
        <text
          x={textX}
          y={Y.page + 15}
          textAnchor="middle"
          fontSize={11}
          fontWeight={700}
          className={segmented ? 'fill-violet-700 dark:fill-violet-300' : 'fill-slate-600 dark:fill-zinc-300'}
        >
          {pageNo}
          {segmented ? ' ✦' : ''}
        </text>
      )}
    </g>
  );
});

export type LeafProps = {
  col: number;
  dimmed: boolean;
  rectX: number;
  rectW: number;
  textX: number;
  isSegment: boolean;
  label: string;
  showText: boolean;
  onEnter: (col: number, e: MouseEvent) => void;
  onOpen: (col: number, e: MouseEvent) => void;
};
export const LeafCellNode = memo(function LeafCellNode({
  col, dimmed, rectX, rectW, textX, isSegment, label, showText, onEnter, onOpen,
}: LeafProps) {
  return (
    <g
      className="cursor-pointer transition-opacity"
      style={{ opacity: dimmed ? 0.18 : 1 }}
      onMouseEnter={(e) => onEnter(col, e)}
      onClick={(e) => onOpen(col, e)}
      onAuxClick={(e) => onOpen(col, e)}
    >
      <rect
        x={rectX}
        y={Y.leaf}
        width={rectW}
        height={Y.leafH}
        rx={4}
        className={clsx(
          'stroke',
          isSegment
            ? 'fill-violet-50 dark:fill-violet-950/40 stroke-violet-200 dark:stroke-violet-900'
            : 'fill-slate-50 dark:fill-zinc-900 stroke-slate-200 dark:stroke-zinc-700',
        )}
      />
      {showText && (
        <text x={textX} y={Y.leaf + 22} textAnchor="middle" fontSize={9} className="fill-slate-500 dark:fill-zinc-400">
          {label}
        </text>
      )}
    </g>
  );
});

export type BandProps = {
  index: number;
  dimmed: boolean;
  rectX: number;
  rectW: number;
  textX: number;
  fill: string;
  showText: boolean;
  label: string;
  onEnter: (index: number, e: MouseEvent) => void;
  onOpen: (index: number, e: MouseEvent) => void;
};
export const BandNode = memo(function BandNode({
  index, dimmed, rectX, rectW, textX, fill, showText, label, onEnter, onOpen,
}: BandProps) {
  return (
    <g
      className="cursor-pointer transition-opacity"
      style={{ opacity: dimmed ? 0.18 : 1 }}
      onMouseEnter={(e) => onEnter(index, e)}
      onClick={(e) => onOpen(index, e)}
      onAuxClick={(e) => onOpen(index, e)}
    >
      <rect x={rectX} y={Y.band} width={rectW} height={Y.bandH} rx={5} style={{ fill }} />
      {showText && (
        <text x={textX} y={Y.band + 17} textAnchor="middle" fontSize={10.5} fontWeight={600} className="fill-white">
          {label}
        </text>
      )}
    </g>
  );
});

export type ConnectorProps = { cx: number; dimmed: boolean };
export const ConnectorLine = memo(function ConnectorLine({ cx, dimmed }: ConnectorProps) {
  return (
    <line
      x1={cx}
      y1={Y.page + Y.pageH}
      x2={cx}
      y2={Y.band}
      className="stroke-slate-200 dark:stroke-zinc-700"
      strokeWidth={1}
      style={{ opacity: dimmed ? 0.12 : 0.5 }}
    />
  );
});
